"use client";

import { Typography } from "@/components/ui/typography";
import { useGetQuoteStore } from "@/store/useGetQuoteStore";
import { memo, useMemo } from "react";
import {
  TRANSLATION_OPTIONS,
  TRANSLATION_RATES,
} from "@/components/screen/get-quote/components/Checkout/constants";
import { formatUSD } from "@/utils";
import { OPTION_LANGUAGES } from "@/services/services";
import {
  OrderCardContent,
  OrderCardItem,
  OrderCardList,
} from "@/components/screen/global-components/order/OrderCard";
import { useStartOrderConfirmedQueryData } from "@/hooks/useStartOrderConfirmedQueryData";

const featureList = [
  "100% Accuracy",
  "Certified Translators",
  "10,000+ Happy Customers",
];

export const ConfirmedOrderCardContent = ({
  className,
}: React.ComponentProps<"div">) => {
  const {
    firstName,
    lastName,
    email,
    translationType,
    fromLanguage,
    toLanguage,
    pageCount,
    wordCount,
    documents,
  } = useStartOrderConfirmedQueryData();

  const isCertified = translationType === TRANSLATION_OPTIONS.certified;

  const accountInfo = useMemo(() => {
    return [
      {
        label: "First Name",
        value: firstName,
      },
      {
        label: "Last Name",
        value: lastName,
      },
      {
        label: "Email Address",
        value: email,
      },
    ];
  }, [firstName, lastName, email]);

  const translationMeta = useMemo(() => {
    if (translationType === "unsure") {
      return {
        type: translationType,
        title: "I’m not sure",
      };
    }

    return {
      type: translationType,
      title: isCertified ? "Certified Translation" : "Standard Translation",
      rate: isCertified
        ? `${formatUSD(TRANSLATION_RATES.certified)} per page`
        : `${formatUSD(TRANSLATION_RATES.standard)} per word`,
      note: "250 max words per page",
    };
  }, [translationType, isCertified]);

  const translationUnitLabel = useMemo(() => {
    if (
      typeof pageCount !== "number" ||
      typeof wordCount !== "number" ||
      !documents ||
      !fromLanguage ||
      !toLanguage
    ) {
      return;
    }

    const translationUnitLabel = `${Number(
      isCertified ? pageCount : wordCount
    )} ${isCertified ? "Pages" : "Words"}`;

    return translationUnitLabel;
  }, [pageCount, wordCount, documents, fromLanguage, toLanguage, isCertified]);

  return (
    <OrderCardContent
      className={className}
      reverse
      topSlot={
        <>
          <Typography variant="captionLg" className="uppercase" asChild>
            <h3>Account Info</h3>
          </Typography>
          <div className="grid grid-cols-2 place-items-start gap-4 md:grid-cols-1 w-full">
            {accountInfo.map(({ label, value }, i) => (
              <OrderCardItem
                key={i}
                label={label}
                size="md"
                direction="column"
                valueClassName="font-bold"
              >
                {value}
              </OrderCardItem>
            ))}
          </div>
        </>
      }
      bottomSlot={<OrderCardList items={featureList} />}
    >
      <div className="grid grid-cols-1 gap-6">
        <Typography variant="captionLg" className="uppercase" asChild>
          <h3>Order Details</h3>
        </Typography>
        <div className="grid grid-cols-1 gap-4 md:gap-6">
          <div className="grid grid-cols-1 place-items-start gap-2">
            <OrderCardItem
              label={translationMeta.title}
              size="md"
              direction="column"
              className="font-bold"
            />
            {!!translationMeta.rate && (
              <OrderCardItem
                label={<>Charged: {translationMeta.rate}</>}
                size="md"
                direction="column"
                className="font-normal"
              />
            )}
            {!!translationMeta.note && (
              <OrderCardItem
                label={<>({translationMeta.note})</>}
                size="md"
                direction="column"
                className="font-normal"
              />
            )}
          </div>
          <div className="grid grid-cols-1 gap-2 w-full">
            <OrderCardItem
              label={
                <>
                  {fromLanguage || "—"} to {toLanguage || "—"}
                </>
              }
              size="md"
              direction="row"
              className="font-bold"
            />
            <OrderCardItem label={translationUnitLabel} />
          </div>
        </div>
      </div>
    </OrderCardContent>
  );
};

export default memo(ConfirmedOrderCardContent);
